import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { PERMISSION_CATALOG } from '../../constants/permissions';
import { UserPermissionService } from '../../services/userPermission.service';
import { useToast } from '../../context/ToastContext';
import { Trans, useTranslation } from 'react-i18next';

const UserPermissions: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const [searchParams] = useSearchParams();
    const institutionId = searchParams.get('institutionId');
    const navigate = useNavigate();
    const toast = useToast();
    const { t } = useTranslation(['users', 'common']);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [checkedKeys, setCheckedKeys] = useState<Set<string>>(new Set());
    const [confirmClearOpen, setConfirmClearOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [clearing, setClearing] = useState(false);

    useEffect(() => {
        if (!userId || !institutionId) return;
        setLoading(true);
        setError(null);
        UserPermissionService.getPermissions(userId, institutionId)
            .then((keys) => {
                setCheckedKeys(new Set(keys));
            })
            .catch((err: any) => {
                setError(err?.response?.data ?? t('userPermissions.loadFailed'));
            })
            .finally(() => setLoading(false));
    }, [userId, institutionId, t]);

    if (!institutionId) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{t('userPermissions.institutionRequired')}</Alert>
            </Box>
        );
    }

    const handleToggleGroup = (subItems: { key: string }[], allChecked: boolean) => {
        setCheckedKeys((prev) => {
            const next = new Set(prev);
            if (allChecked) {
                subItems.forEach((s) => next.delete(s.key));
            } else {
                subItems.forEach((s) => next.add(s.key));
            }
            return next;
        });
    };

    const handleToggle = (key: string) => {
        setCheckedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const handleSave = async () => {
        if (!userId || !institutionId) return;
        setSaving(true);
        try {
            await UserPermissionService.setPermissions(userId, institutionId, Array.from(checkedKeys));
            toast.showSuccess(t('userPermissions.saved'));
        } catch (err: any) {
            toast.showApiError(err, t('userPermissions.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    const handleClearConfirm = async () => {
        if (!userId || !institutionId) return;
        setClearing(true);
        try {
            await UserPermissionService.clearPermissions(userId, institutionId);
            setCheckedKeys(new Set());
            toast.showSuccess(t('userPermissions.cleared'));
        } catch (err: any) {
            toast.showApiError(err, t('userPermissions.clearFailed'));
        } finally {
            setClearing(false);
            setConfirmClearOpen(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: 700 }}>
            <Typography variant="h5" gutterBottom>
                {t('userPermissions.title')}
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Alert severity="info" sx={{ mb: 3 }}>
                {checkedKeys.size > 0 ? (
                    <Trans t={t} i18nKey="userPermissions.manualMode" components={{ strong: <strong /> }} />
                ) : (
                    <Trans t={t} i18nKey="userPermissions.roleMode" components={{ strong: <strong /> }} />
                )}
            </Alert>

            {PERMISSION_CATALOG.map((group) => (
                <Box key={group.group} sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {t(`permissionCatalog.groups.${group.groupKey}`)}
                    </Typography>
                    <FormGroup>
                        {group.items.map((item) => {
                            if (item.subItems) {
                                const allChecked = item.subItems.every((s) => checkedKeys.has(s.key));
                                const someChecked = item.subItems.some((s) => checkedKeys.has(s.key));
                                return (
                                    <Box key={item.key}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={allChecked}
                                                    indeterminate={someChecked && !allChecked}
                                                    onChange={() => handleToggleGroup(item.subItems!, allChecked)}
                                                />
                                            }
                                            label={t(`permissionCatalog.labels.${item.key}`)}
                                        />
                                        <FormGroup sx={{ pl: 4 }}>
                                            {item.subItems.map((sub) => (
                                                <FormControlLabel
                                                    key={sub.key}
                                                    control={
                                                        <Checkbox
                                                            checked={checkedKeys.has(sub.key)}
                                                            onChange={() => handleToggle(sub.key)}
                                                        />
                                                    }
                                                    label={t(`permissionCatalog.labels.${sub.key}`)}
                                                />
                                            ))}
                                        </FormGroup>
                                    </Box>
                                );
                            }
                            return (
                                <FormControlLabel
                                    key={item.key}
                                    control={
                                        <Checkbox
                                            checked={checkedKeys.has(item.key)}
                                            onChange={() => handleToggle(item.key)}
                                        />
                                    }
                                    label={t(`permissionCatalog.labels.${item.key}`)}
                                />
                            );
                        })}
                    </FormGroup>
                </Box>
            ))}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? t('common:status.saving') : t('userPermissions.save')}
                </Button>
                <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => setConfirmClearOpen(true)}
                    disabled={clearing}
                >
                    {t('userPermissions.clearAllRevert')}
                </Button>
                <Button variant="text" onClick={() => navigate('/users')}>
                    {t('userPermissions.backToUsers')}
                </Button>
            </Box>

            <Dialog open={confirmClearOpen} onClose={() => setConfirmClearOpen(false)}>
                <DialogTitle>{t('userPermissions.confirmTitle')}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t('userPermissions.confirmText')}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmClearOpen(false)} variant="outlined">
                        {t('common:actions.cancel')}
                    </Button>
                    <Button
                        onClick={handleClearConfirm}
                        variant="contained"
                        color="warning"
                        disabled={clearing}
                    >
                        {clearing ? t('userPermissions.clearing') : t('userPermissions.clearAll')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UserPermissions;
